import type { RefObject } from "react";
import { Button } from "@datapay-by-datacom/design-system";
import { FileDropzone } from "../../shared/components";
import {
  CLIENT_PAY_NORMALISATION_PDF_ACCEPT,
  CLIENT_PAY_NORMALISATION_PDF_SUB_INSTRUCTION,
} from "../config";
import { SecurityNoticeBanner } from "./SecurityNoticeBanner";
import s from "./PayRunPdfUploadSection.module.css";

export interface PayRunPdfUploadSectionProps {
  sectionRef: RefObject<HTMLElement | null>;
  sourceSelected: boolean;
  sourceDisplayName: string | null;
  payPeriodLabel: string;
  file: File | null;
  fileTypeError: string | null;
  onFileChange: (file: File | null) => void;
  onNormalise: () => void;
  isNormalising?: boolean;
  submitError?: string | null;
}

export function PayRunPdfUploadSection({
  sectionRef,
  sourceSelected,
  sourceDisplayName,
  payPeriodLabel,
  file,
  fileTypeError,
  onFileChange,
  onNormalise,
  isNormalising = false,
  submitError = null,
}: PayRunPdfUploadSectionProps) {
  const dropzoneDisabled = !sourceSelected || isNormalising;
  const canNormalise =
    sourceSelected && file !== null && fileTypeError === null && !isNormalising;

  const periodSuffix =
    payPeriodLabel.trim() !== ""
      ? ` · Label: ${payPeriodLabel.trim()}`
      : "";

  return (
    <section
      ref={sectionRef}
      className={s.section}
      aria-labelledby="upload-pdf-heading"
      tabIndex={-1}
    >
      <h2 id="upload-pdf-heading" className={s.heading}>
        Upload payslip PDF
      </h2>

      {sourceDisplayName !== null && (
        <p className={s.subtitle}>
          Source: <strong>{sourceDisplayName}</strong>
          {periodSuffix}
        </p>
      )}

      <SecurityNoticeBanner />

      {submitError !== null && (
        <div className={s.errorBanner} role="alert">
          {submitError}
        </div>
      )}

      {sourceSelected && fileTypeError !== null && (
        <div className={s.errorBanner} role="alert">
          {fileTypeError}
        </div>
      )}

      <FileDropzone
        value={file}
        onChange={(next) => {
          if (next === null || next instanceof File) {
            onFileChange(next);
          }
        }}
        instruction="Drop combined payslip PDF here, or click to browse"
        subInstruction={CLIENT_PAY_NORMALISATION_PDF_SUB_INSTRUCTION}
        accept={CLIENT_PAY_NORMALISATION_PDF_ACCEPT}
        multiple={false}
        isDisabled={dropzoneDisabled}
        buttonText="Select PDF file"
        disabledButtonText={
          sourceSelected ? "Processing…" : "Select an import source first"
        }
        compactWhenSelected
      />

      {file !== null && sourceSelected && fileTypeError === null && (
        <p className={s.readyPill} role="status">
          <span className={s.readyBadge}>Ready to normalise</span>
        </p>
      )}

      <div className={s.actions}>
        <Button
          variant="primary"
          onPress={onNormalise}
          isDisabled={!canNormalise}
        >
          Normalise
        </Button>
      </div>
    </section>
  );
}
